import { Select, SelectItem } from '@carbon/react';
import { DateRangeFilter, PageHeader, StatusTag, useFilters, useFormat, useReferenceOptions } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useState } from 'react';
import { hoApi } from '../api';
import { dayRange, setOnly, shortId } from './dates';
import { MultiChoice } from './fields';
import { LogDetail } from './LogDetail';
import { LogTable, type LogQuery } from './LogTable';

type Call = HoComponents['schemas']['ProviderCallDto'];

/** The outcomes as the shared StatusTag colours them: worked, will be tried again, or someone has to look. */
const OUTCOME_TONE: Record<string, string> = {
  ok: 'approved',
  unavailable: 'pending',
  uncertain: 'open',
  refused: 'failed',
  account_rejected: 'declined',
  rejected: 'declined',
  error: 'failed',
};
const OUTCOMES = Object.keys(OUTCOME_TONE);
const CAPABILITIES = ['shipping', 'payment', 'messaging', 'einvoice'];
const logsKey = ['ho', 'integration-logs'] as const;

/** The last day, when no dates are chosen: the log is read a window of at most 31 days at a time. */
function windowOf(from: string, to: string) {
  const range = dayRange(from, to);
  const end = range.to ?? new Date(Date.now() + 60_000).toISOString();
  const start = range.from ?? new Date(new Date(end).getTime() - 86_400_000).toISOString();
  return { from: start, to: end };
}

/**
 * Every call to a delivery, payment, messaging or e-invoice provider (ADR 0113): what was asked, how it ended, how long it took,
 * and what it was for. Search by any reference (our order number, the provider's id); a call opens with its HTTP exchanges and
 * the whole history of the booking, payment, message or invoice it was for.
 */
export function IntegrationLogsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const stores = useReferenceOptions('org.store');
  const storeName = (id: string | null | undefined) => (id ? (stores.data?.find((s) => s.value === id)?.label ?? shortId(id)) : '');
  const filters = useFilters({ capability: '', outcome: '', store: '', from: '', to: '' });
  const [open, setOpen] = useState<Call>();
  const outcomeName = (o: string) => t(`logs.integration.outcomes.${o}` as I18nKey, { defaultValue: o });
  const capabilityName = (c: string) => t(`logs.integration.capabilities.${c}` as I18nKey, { defaultValue: c });

  const load = async (q: LogQuery) => {
    const { capability, outcome, store, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/integration/calls', {
      params: { query: { ...windowOf(from ?? '', to ?? ''), ...setOnly({ capability, outcome, storeId: store }), ...q } },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('logs.integration.title')} description={t('logs.integration.hint')} />
      <LogTable<Call>
        label={t('logs.integration.title')}
        queryKey={logsKey}
        load={load}
        rowId={(c) => c.id}
        filters={filters}
        onOpen={setOpen}
        filterFields={
          <>
            <Select
              id="calls-capability"
              labelText={t('logs.integration.columns.capability')}
              value={filters.draft.capability}
              onChange={(e) => filters.set('capability', e.target.value)}
            >
              <SelectItem value="" text={t('logs.integration.any')} />
              {CAPABILITIES.map((c) => (
                <SelectItem key={c} value={c} text={capabilityName(c)} />
              ))}
            </Select>
            <MultiChoice
              id="calls-outcome"
              label={t('logs.integration.columns.outcome')}
              choices={OUTCOMES.map((o) => ({ id: o, label: outcomeName(o) }))}
              value={filters.draft.outcome}
              onChange={(v) => filters.set('outcome', v)}
            />
            <Select
              id="calls-store"
              labelText={t('logs.columns.store')}
              value={filters.draft.store}
              onChange={(e) => filters.set('store', e.target.value)}
            >
              <SelectItem value="" text={t('logs.integration.any')} />
              {(stores.data ?? []).map((s) => (
                <SelectItem key={s.value} value={s.value} text={s.label} />
              ))}
            </Select>
            <DateRangeFilter
              id="calls-date"
              legendText={t('logs.integration.columns.at')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        columns={[
          { key: 'at', header: t('logs.integration.columns.at'), render: (c) => format.dateTime(c.at) },
          { key: 'provider', header: t('logs.integration.columns.provider'), render: (c) => c.accountName ?? c.providerCode },
          { key: 'operation', header: t('logs.integration.columns.operation'), render: (c) => c.operation },
          {
            key: 'outcome',
            header: t('logs.integration.columns.outcome'),
            render: (c) => (
              <>
                <StatusTag status={OUTCOME_TONE[c.outcome] ?? 'failed'}>{outcomeName(c.outcome)}</StatusTag>
                {c.errorCode && <p className="pos-log__error">{c.errorCode}</p>}
              </>
            ),
          },
          {
            key: 'duration',
            header: t('logs.integration.columns.duration'),
            render: (c) => <span className="pos-log__number">{t('logs.integration.ms', { ms: c.durationMs })}</span>,
          },
          { key: 'store', header: t('logs.columns.store'), render: (c) => storeName(c.storeId) },
          {
            key: 'record',
            header: t('logs.columns.record'),
            render: (c) => (c.relatedType ? <span className="pos-log__id">{`${c.relatedType} ${shortId(c.relatedId)}`}</span> : ''),
          },
        ]}
      />
      {open && (
        <LogDetail
          title={`${open.accountName ?? open.providerCode} · ${open.operation}`}
          label={t('logs.integration.title')}
          queryKey={[...logsKey, 'detail', open.id]}
          // The exchanges, the references, and everything else that happened to the same record, oldest first.
          loadPayload={async () => {
            const timeline = open.relatedId
              ? await hoApi.GET('/api/v1/integration/calls/timeline', { params: { query: { relatedId: open.relatedId } } })
              : null;
            if (timeline && !timeline.data) throw timeline.error;
            return { http: open.http, references: open.refs, timeline: timeline?.data ?? [] };
          }}
          onClose={() => setOpen(undefined)}
          fields={[
            [t('logs.integration.columns.at'), format.dateTime(open.at)],
            [t('logs.integration.columns.capability'), capabilityName(open.capability)],
            [t('logs.integration.columns.provider'), `${open.providerCode}${open.accountName ? ` (${open.accountName})` : ''}`],
            [t('logs.integration.columns.operation'), open.operation],
            [t('logs.integration.columns.outcome'), outcomeName(open.outcome)],
            [t('logs.columns.error'), [open.errorCode, open.message].filter(Boolean).join(': ') || null],
            [t('logs.integration.columns.duration'), t('logs.integration.ms', { ms: open.durationMs })],
            [t('logs.columns.store'), storeName(open.storeId) || null],
            [t('logs.columns.record'), open.relatedType ? `${open.relatedType} ${open.relatedId}` : null],
            [t('logs.integration.trace'), open.traceId],
          ]}
        />
      )}
    </section>
  );
}
